import {
  ClientSettingsContext,
  StateContext,
  getStartingSimulationHorizon as getTotalBudget,
} from "@client/pages/round/Round";
import { Funder, Recommender } from "@engine/round/aggregate";
import React, { useContext, useEffect, useState } from "react";

import { Tooltip } from "@client/components/utils/Tooltip";
import {
  otherUnilateralAllocation,
  unilateralAllocation,
} from "@client/utils/tooltips";
import { EditableNumberField } from "../TrancheListEditor/EditableNumberField";

export const AllocationChartTitle = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div className="p-2 text-center all-small-caps font-semibold dark:font-normal">
      {children}
    </div>
  );
};

const PlayCircle = () => (
  <svg
    className="py-[3px] w-[1.5em] dark:fill-white"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
  >
    <path d="M12,1A11,11,0,1,0,23,12,11.013,11.013,0,0,0,12,1Zm0,20a9,9,0,1,1,9-9A9.011,9.011,0,0,1,12,21ZM10,8l6,4-6,4Z" />
  </svg>
);

const PauseCircle = () => (
  <svg
    className="py-[3px] w-[1.5em] dark:fill-white"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
  >
    <path d="M12,1A11,11,0,1,0,23,12,11.013,11.013,0,0,0,12,1Zm0,20a9,9,0,1,1,9-9A9.011,9.011,0,0,1,12,21ZM9,8h2v8H9Zm4,0h2v8H13Z" />
  </svg>
);

const useSweepBudgetControls = (
  simulationHorizon: number,
  maxSimulationHorizon: number,
  setSimulationHorizon: (n: number) => void,
) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  function toggleSweep() {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }

    if (simulationHorizon >= maxSimulationHorizon) {
      setSimulationHorizon(0);
    }

    setIsPlaying(true);
  }

  function stopSweep() {
    setIsPlaying(false);
  }

  useEffect(() => {
    const sweepStep = maxSimulationHorizon / 100;
    let currentSimulationHorizon = simulationHorizon;

    if (isPlaying) {
      const timer = window.setInterval(() => {
        if (currentSimulationHorizon === maxSimulationHorizon) {
          setIsPlaying(false);
          clearInterval(timer);
          return;
        }
        currentSimulationHorizon += sweepStep;
        setSimulationHorizon(
          Math.min(currentSimulationHorizon, maxSimulationHorizon),
        );
      }, 50);

      return () => {
        clearInterval(timer);
      };
    }
  }, [
    isPlaying,
    maxSimulationHorizon,
    setSimulationHorizon,
    simulationHorizon,
  ]);

  return { toggleSweep, stopSweep, isPlaying };
};

export function AllocationChartHeader({
  evaluator,
}: {
  evaluator: Recommender | Funder;
}) {
  const { round, actingAsUserId: userId } = React.useContext(StateContext);
  const { getSimulationHorizon, setSimulationHorizon } = useContext(
    ClientSettingsContext,
  );

  const [maxSimulationHorizon, setMaxSimulationHorizon] = useState(
    getTotalBudget(round),
  );

  const simulationHorizon = getSimulationHorizon(round);

  const { toggleSweep, stopSweep, isPlaying } = useSweepBudgetControls(
    simulationHorizon,
    maxSimulationHorizon,
    setSimulationHorizon,
  );

  const matchingMember = round.members[evaluator.memberId];

  return (
    <div className="bg-stone-400 dark:bg-stone-700 text-sm pb-3">
      <AllocationChartTitle>
        <div className="flex justify-between items-center">
          <Tooltip
            title={
              matchingMember!.id === userId
                ? unilateralAllocation
                : otherUnilateralAllocation(matchingMember!.initials)
            }
          >
            <div>{`${matchingMember!.initials}'s unilateral allocation`}</div>
          </Tooltip>
        </div>
      </AllocationChartTitle>
      <div className="flex px-2">
        <span onClick={() => toggleSweep()}>
          {isPlaying ? <PauseCircle /> : <PlayCircle />}
        </span>
        <input
          className="w-full border-0 p-0"
          type="range"
          min={0}
          step={1000}
          max={maxSimulationHorizon}
          value={simulationHorizon}
          onChange={(e) => {
            stopSweep();
            setSimulationHorizon(+e.target.value);
          }}
        />
      </div>
      <div className="grid grid-cols-3">
        <div />
        <EditableNumberField
          className="max-w-20 mx-auto"
          isMoney={true}
          value={simulationHorizon}
          hideIfZero={false}
          onSubmitted={(value) => {
            stopSweep();
            setSimulationHorizon(value);
          }}
          min={0}
          max={maxSimulationHorizon}
          onFocus={() => {
            stopSweep();
          }}
        />
        <EditableNumberField
          className="max-w-20 ml-auto"
          isMoney={true}
          value={maxSimulationHorizon}
          hideIfZero={false}
          onSubmitted={(value) => {
            stopSweep();
            setMaxSimulationHorizon(value);
          }}
          min={0}
          max={1.0e10}
          onFocus={() => {
            stopSweep();
          }}
        />
      </div>
    </div>
  );
}

export default AllocationChartHeader;
